import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminApi } from "@/lib/auth";
import { errorText } from "@/lib/errors";
import { Loading } from "./shared";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const DayRow = ({ day, times, allowed, onChange }) => {
  const free = allowed.filter((t) => !times.includes(t));
  const add = (t) => onChange([...times, t].sort((a, b) => allowed.indexOf(a) - allowed.indexOf(b)));
  return (
    <li className="py-5 grid sm:grid-cols-[140px_1fr_auto] gap-3 items-center" data-testid={`schedule-day-${day}`}>
      <p className="dp text-2xl">{DAYS[day]}</p>
      <div className="flex flex-wrap gap-2 tn">
        {times.length === 0 && <span className="text-sm text-moss">No coaching this day</span>}
        {times.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-sage px-3 py-1.5 text-sm">
            {t}
            <button type="button" aria-label={`Remove ${t}`} onClick={() => onChange(times.filter((x) => x !== t))}
              className="hover:text-red-800" data-testid={`schedule-remove-${day}-${t.replace(/[^0-9APM]/g, "")}`}><X size={14} /></button>
          </span>
        ))}
      </div>
      <Select value="" onValueChange={add}>
        <SelectTrigger className="w-[150px] rounded-full bg-transparent border-pine/40" data-testid={`schedule-add-${day}`}>
          <SelectValue placeholder="+ Add time" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {free.map((t) => <SelectItem key={t} value={t} data-testid={`schedule-option-${day}-${t.replace(/[^0-9APM]/g, "")}`}>{t}</SelectItem>)}
        </SelectContent>
      </Select>
    </li>
  );
};

export const ScheduleTab = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/schedule").then(({ data: d }) => setData(d)).catch((err) => toast.error(errorText(err)));
  }, []);

  if (!data) return <Loading />;
  const setDay = (day) => (times) => setData({ ...data, weekdays: { ...data.weekdays, [day]: times } });

  const save = async () => {
    setSaving(true);
    try {
      setData((await adminApi.put("/admin/schedule", { weekdays: data.weekdays })).data);
      toast.success("Coaching hours saved. Couples now see these times.");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-paper rounded-2xl p-6 sm:p-8" data-testid="admin-schedule">
      <p className="text-moss">Each time is a 50-minute session, in Central Time. Already-booked sessions stay booked if you remove a time.</p>
      <ul className="mt-4 divide-y divide-pine/15">
        {DAYS.map((_, d) => <DayRow key={d} day={d} times={data.weekdays[String(d)] || []} allowed={data.allowed_times} onChange={setDay(String(d))} />)}
      </ul>
      <button type="button" onClick={save} disabled={saving} className="mt-6 rounded-full bg-pine text-sage px-8 py-3 disabled:opacity-70" data-testid="schedule-save-button">
        {saving ? "Saving..." : "Save coaching hours"}
      </button>
    </div>
  );
};
